import { useGetAgendaViewRecordsQuery } from 'store/services/flow/agendaRecords';
import type { AgendaRecordItem, GetAgendaRecordsRes } from 'types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { getValuesByFormItemId } from 'utils/agendaRecord/agendaRecord.ts';
import { isObjectArray } from 'utils/typeChecks';
import { isNormalizedValueChanged } from 'utils/common';
import type { SelectedRow, SelectedRowInput } from './selectedRows';
import type { RecordsItemProps } from '../components/RecordsItem';

interface UseRecordsItemArgs {
  formItem: RecordsItemProps;
  moduleId?: string;
  systemType?: string;
  onConfirm?: () => void;
  formOptionShouldDirty?: boolean;
}

export const useRecordsItem = ({ formItem, moduleId = '', systemType = '', onConfirm, formOptionShouldDirty }: UseRecordsItemArgs) => {
  const navigate = useNavigate();

  const { setValue, register, trigger: triggerForm, control } = useFormContext();
  const { viewId, agendaId, formItemIdToShow, params, isMultiSelect = false } = formItem.definition.records;
  const [modalState, setModalState] = useState({
    open: false,
    searchTerm: '',
    isAutoSelectSearchResultEnabled: false,
  });
  const [selectedRows, ...paramValues] = useWatch({
    control,
    name: [formItem.id, ...(params ?? []).map(({ valueFormItemId }: { valueFormItemId: string }) => valueFormItemId)],
  });
  const isInitialized = useRef(false);
  const oldParamValues = useRef(paramValues);

  useEffect(() => {
    if (!isInitialized.current) return;

    if (paramValues.some((value) => value === undefined)) {
      return;
    }

    if (paramValues.map((value, index) => isNormalizedValueChanged(value, oldParamValues.current[index])).some(Boolean)) {
      oldParamValues.current = paramValues;
      setValue(formItem.id, [], {
        shouldDirty: formOptionShouldDirty ?? false,
        shouldTouch: false,
      });
    }
  }, [paramValues, formItem.id, setValue, selectedRows, formOptionShouldDirty]);

  useEffect(() => {
    register(formItem.id, formItem.registerProps);
  }, [formItem.id, formItem.registerProps, register]);

  const validateOnBlur = useCallback(() => {
    triggerForm(formItem.id);
  }, [formItem.id, triggerForm]);

  const selectedRecordIds = isObjectArray(selectedRows ?? [])
    ? selectedRows.map(({ id }: { id: string }) => id).filter(Boolean)
    : (selectedRows ?? []);

  const { data: { items = [] } = {} as GetAgendaRecordsRes<AgendaRecordItem[]> } = useGetAgendaViewRecordsQuery(
    {
      params: {
        agendaId,
        viewId,
        page: 1,
        pageSize: 1000,
        search: '',
        orderBy: '',
        formItemId: '',
        orderDesc: false,
      },
      recordIds: selectedRecordIds,
      skipViewConditions: true,
      evaluateVisualAttributes: false,
      valueFormItems: [],
      filters: [],
    },
    { skip: !selectedRows || selectedRows?.length === 0, refetchOnMountOrArgChange: true }
  );

  const displayAttributeByRecordId = useMemo(() => {
    const map = new Map<string, AgendaRecordItem['attributes'][number] | undefined>();
    items.forEach((item) => {
      map.set(
        item.id,
        item.attributes.find((a) => a.formItem?.id === formItemIdToShow)
      );
    });
    return map;
  }, [items, formItemIdToShow]);

  const resolvedDisplayValues = useMemo(() => {
    if (!selectedRecordIds.length) {
      return [];
    }

    return getValuesByFormItemId(items, formItemIdToShow, true);
  }, [items, formItemIdToShow, selectedRecordIds.length]);

  useEffect(() => {
    if (!selectedRows?.length) return;

    const needsTransformation = selectedRows.every((row: SelectedRowInput) => typeof row === 'string');
    if (!needsTransformation) return;

    const selectedIds = selectedRows as string[];
    const filteredItems = (items ?? []).filter((item) => selectedIds.includes(item.id));
    const allFetched = selectedIds.every((id) => filteredItems.some((item) => item.id === id));
    if (!allFetched) return;

    const getValues = getValuesByFormItemId(filteredItems, formItemIdToShow, true);
    if (getValues.length > 0) {
      isInitialized.current = true;
      oldParamValues.current = paramValues;
      setValue(formItem.id, getValues, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [formItem.id, setValue, selectedRows, paramValues, items, formItemIdToShow]);

  const displayValues = useMemo<SelectedRow[]>(() => {
    if (!resolvedDisplayValues?.length) return [];

    return resolvedDisplayValues.map((item) => {
      if (typeof item === 'string') {
        return { id: '', value: item };
      }

      const displayAttribute = displayAttributeByRecordId.get(item.id);
      const isUserList = displayAttribute?.formItem?.definition?.type === 'Users';
      const value = Array.isArray(item.value)
        ? isUserList
          ? item.value.join('; ')
          : typeof item.value[0] === 'string'
            ? item.value[0]
            : item.value[0]?.text
        : item.value;

      return {
        id: item.id,
        value: value || '',
        formItemUiDefinition: displayAttribute?.visibilityResult?.formItemUiDefinition ?? null,
        wrapValue: isUserList,
      };
    });
  }, [resolvedDisplayValues, displayAttributeByRecordId]);

  const firstSelectedRecordId = displayValues[0]?.id ?? '';

  const isEditable = !formItem.detail && !formItem.disabled;

  const chipRows = useMemo(
    () => (isMultiSelect ? displayValues : displayValues.filter((item) => isEditable && !item.value)),
    [displayValues, isEditable, isMultiSelect]
  );

  const toggleModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      open: !prev.open,
      isAutoSelectSearchResultEnabled: false,
    }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      open: false,
      isAutoSelectSearchResultEnabled: false,
    }));
  }, []);

  const handleOnClick = useCallback(
    (id?: string) => {
      if (!formItem.detail) return toggleModal();

      const url = `/user/modules/${moduleId}/${systemType}/${agendaId}/r/${id ?? firstSelectedRecordId}/detail`;
      navigate(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agendaId, formItem.detail, moduleId, firstSelectedRecordId, systemType]
  );

  const handleSearchSubmit = useCallback((searchTerm: string, shouldAutoSelect: boolean) => {
    setModalState({
      open: true,
      searchTerm,
      isAutoSelectSearchResultEnabled: shouldAutoSelect,
    });
  }, []);

  const handleClear = useCallback(() => {
    setValue(formItem.id, [], { shouldDirty: true });
    triggerForm(formItem.id);
    onConfirm?.();
  }, [formItem.id, onConfirm, setValue, triggerForm]);

  const handleAutoSelectConsumed = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isAutoSelectSearchResultEnabled: false,
    }));
  }, []);

  const handleModalSubmit = useCallback(
    (value: SelectedRow[]) => {
      handleCloseModal();
      setValue(formItem.id, value, { shouldDirty: true });
      triggerForm(formItem.id);
      onConfirm?.();
    },
    [formItem.id, setValue, triggerForm, onConfirm, handleCloseModal]
  );

  return {
    formItemIdToShow,
    isMultiSelect,
    isEditable,
    selectedRows,
    displayValues,
    chipRows,
    modalState,
    validateOnBlur,
    handleOnClick,
    handleSearchSubmit,
    handleClear,
    handleAutoSelectConsumed,
    handleCloseModal,
    handleModalSubmit,
  };
};
